/**
 * Account-pair letter helpers for the admin UI.
 *
 * `PAIR_LETTERS` mirrors `account_pair_manager.py::_VALID_PAIR_LETTERS`.
 * Retired letters stay in the vocabulary (a pair that ever carried a
 * conversation can never leave it) but the backend reports them in
 * `retired_letters`, and the admin UI must hide them on EVERY pair surface.
 *
 * Every admin pair surface derives its letter list from `visiblePairLetters`
 * (one place), so a new letter or a newly retired letter needs no per-surface
 * edit. Old backend (no `retired_letters` field) -> degrade to the letters the
 * response actually carries, unfiltered.
 *
 * NOTE: only `import type` from services/api -- api.ts imports this module at
 * runtime, and AdminPage tests jest.mock() services/api wholesale.
 */
import type { PairConfigResponse, PairCooldownEntry, PairCooldownResponse } from '../services/api';

export const PAIR_LETTERS = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j'] as const;
export type PairLetter = typeof PAIR_LETTERS[number];

/** `pair-x` label for a letter (the backend's `_VALID_PAIR_LABELS` spelling). */
export const pairLabel = (letter: PairLetter): string => `pair-${letter}`;

/**
 * Retired-letter set reported by the backend, or null when NO loaded response
 * carries the field (old backend / not loaded yet) -> caller must not filter.
 */
export function retiredPairLetters(
  config: PairConfigResponse | null | undefined,
  cooldown: PairCooldownResponse | null | undefined,
): Set<string> | null {
  const list = config?.retired_letters ?? cooldown?.retired_letters;
  return Array.isArray(list) ? new Set(list.map((l) => String(l).toLowerCase())) : null;
}

/**
 * The ONE visible-letter list: vocabulary letters present in a loaded
 * response, minus `retired_letters`. Empty until a response has loaded.
 */
export function visiblePairLetters(
  config: PairConfigResponse | null | undefined,
  cooldown: PairCooldownResponse | null | undefined,
): PairLetter[] {
  const retired = retiredPairLetters(config, cooldown);
  const cfg = config as unknown as Record<string, unknown> | null | undefined;
  const cd = cooldown as unknown as Record<string, unknown> | null | undefined;
  return PAIR_LETTERS.filter((letter) => {
    if (retired && retired.has(letter)) return false;
    const inConfig = typeof cfg?.[`weight_${letter}`] === 'number';
    const inCooldown = cd?.[`pair_${letter}`] != null;
    return inConfig || inCooldown;
  });
}

/**
 * Pair letter a token-status / token-refresh `host` label belongs to, or null.
 * Labels are `<host>:pair-x` (see admin.py::get_token_status); a label without
 * a pair suffix (single-account "local" etc.) returns null and is never hidden.
 */
export function pairLetterOfHostLabel(host: string): string | null {
  const m = /:pair-([a-z])$/.exec(host);
  return m ? m[1] : null;
}

/** True when the host label belongs to a retired pair (=> hide the row). */
export function isRetiredHostLabel(host: string, retired: Set<string> | null): boolean {
  if (!retired) return false;
  const letter = pairLetterOfHostLabel(host);
  return letter !== null && retired.has(letter);
}

/** `weight_<l>` of a pair-config response, or undefined when absent. */
export function pairConfigWeight(
  config: PairConfigResponse | null | undefined,
  letter: PairLetter,
): number | undefined {
  const v = (config as unknown as Record<string, unknown> | null | undefined)?.[`weight_${letter}`];
  return typeof v === 'number' ? v : undefined;
}

/** `pair_<l>` entry of a pair-cooldown response, or undefined when absent. */
export function pairCooldownEntry(
  cooldown: PairCooldownResponse | null | undefined,
  letter: PairLetter,
): PairCooldownEntry | undefined {
  const v = (cooldown as unknown as Record<string, unknown> | null | undefined)?.[`pair_${letter}`];
  return v && typeof v === 'object' ? (v as PairCooldownEntry) : undefined;
}
